import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminAgreementAPI } from '../services/api';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
  Stack,
} from '@mui/material';

const ALL_ROLE_OPTIONS = [
  'pmp_admin',
  'site_admin',
  'client_admin',
  'client_user',
  'client_finance_user',
  'vendor_admin',
  'vendor_staff',
  'medical_user',
  'internal_staff',
];

const AdminAgreementEdit: React.FC = () => {
  const { agreementId } = useParams();
  const navigate = useNavigate();
  const isNew = agreementId === 'new';

  const [form, setForm] = useState<any>({
    title: '',
    slug: '',
    version: 1,
    role_mapping: ['client_user'],
    is_active: true,
    force_resign: false,
    html_content: '<p>Agreement HTML goes here...</p>',
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const canSave = useMemo(() => form.title && form.slug && form.html_content, [form]);

  const load = async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const res = await adminAgreementAPI.get(Number(agreementId));
      setForm(res.data.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agreementId]);

  const handleChange = (key: string, value: any) => {
    setForm((prev: any) => ({ ...prev, [key]: value }));
  };

  const handleRoleToggle = (role: string) => {
    setForm((prev: any) => {
      const exists = prev.role_mapping.includes(role);
      return {
        ...prev,
        role_mapping: exists
          ? prev.role_mapping.filter((r: string) => r !== role)
          : [...prev.role_mapping, role],
      };
    });
  };

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      if (isNew) {
        await adminAgreementAPI.create(form);
      } else {
        await adminAgreementAPI.update(Number(agreementId), form);
      }
      navigate('/admin/agreements');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Card>
        <CardHeader title={isNew ? 'Create Agreement' : 'Edit Agreement'} />
        <Divider />
        <CardContent>
          {loading ? (
            <Typography>Loading...</Typography>
          ) : (
            <Stack spacing={2}>
              <TextField
                label="Title"
                value={form.title}
                onChange={(e) => handleChange('title', e.target.value)}
                fullWidth
              />
              <TextField
                label="Slug"
                value={form.slug}
                onChange={(e) => handleChange('slug', e.target.value)}
                fullWidth
                disabled={!isNew}
                helperText={isNew ? 'Unique identifier (cannot be changed later)' : ''}
              />
              <TextField
                label="Version"
                type="number"
                value={form.version}
                onChange={(e) => handleChange('version', Number(e.target.value))}
                fullWidth
                inputProps={{ min: 1 }}
              />

              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Applicable Roles
                </Typography>
                <FormGroup row>
                  {ALL_ROLE_OPTIONS.map((role) => (
                    <FormControlLabel
                      key={role}
                      control={
                        <Switch
                          checked={form.role_mapping.includes(role)}
                          onChange={() => handleRoleToggle(role)}
                        />
                      }
                      label={role}
                    />
                  ))}
                </FormGroup>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  {form.role_mapping.map((role: string) => (
                    <Chip key={role} size="small" label={role} />
                  ))}
                </Stack>
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={form.is_active}
                    onChange={(e) => handleChange('is_active', e.target.checked)}
                  />
                }
                label="Active"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={form.force_resign}
                    onChange={(e) => handleChange('force_resign', e.target.checked)}
                  />
                }
                label="Force re-sign for applicable users"
              />

              <TextField
                label="Agreement HTML"
                value={form.html_content}
                onChange={(e) => handleChange('html_content', e.target.value)}
                fullWidth
                multiline
                minRows={12}
                helperText="Supports tokens like [CURRENT_DATE], [ORGANIZATION_FIRM], [NAME], etc."
              />

              <Stack direction="row" spacing={2}>
                <Button variant="outlined" onClick={() => navigate('/admin/agreements')}>Cancel</Button>
                <Button variant="contained" onClick={handleSave} disabled={!canSave || saving}>
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </Stack>
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AdminAgreementEdit;
